import * as React from "react";
import { FileIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button, ButtonProps } from "@/components/_organisms/tiptap/tiptap-ui-primitive/button";
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
import { type Editor } from "@tiptap/react";

export interface PdfUploadButtonProps extends ButtonProps {
  editor?: Editor | null;
  extensionName?: string;
}

export function isPdfActive(editor: Editor | null, extensionName: string): boolean {
  if (!editor) return false;
  return editor.isActive(extensionName);
}

export function insertPdf(editor: Editor | null, extensionName: string): boolean {
  if (!editor) {
    return false;
  }

  const result = editor
    .chain()
    .focus()
    .insertContentAt(editor.state.selection.to, {
      type: extensionName,
    })
    .run();

  return result;
}

export function usePdfUploadButton(
  editor: Editor | null,
  extensionName: string = "pdfUpload",
  disabled: boolean = false,
) {
  const isActive = isPdfActive(editor, extensionName);
  const handleInsertPdf = React.useCallback(() => {
    if (disabled) return false;
    return insertPdf(editor, extensionName);
  }, [editor, extensionName, disabled]);

  return {
    isActive,
    handleInsertPdf,
  };
}

export const PdfUploadButton = React.forwardRef<HTMLButtonElement, PdfUploadButtonProps>(
  (
    {
      editor: providedEditor,
      extensionName = "pdfUpload",
      className = "",
      disabled,
      onClick,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    const { t } = useTranslation("common");
    const editor = useTiptapEditor(providedEditor);
    const { isActive, handleInsertPdf } = usePdfUploadButton(editor, extensionName, disabled);

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);

        if (!e.defaultPrevented && !disabled) {
          handleInsertPdf();
        }
      },
      [onClick, disabled, handleInsertPdf],
    );

    if (!editor || !editor.isEditable) {
      return null;
    }

    return (
      <Button
        ref={ref}
        type="button"
        className={className.trim()}
        data-style="ghost"
        data-active-state={isActive ? "on" : "off"}
        role="button"
        aria-label={t("editor.addPdf")}
        aria-pressed={isActive}
        tooltip={t("editor.addPdf")}
        onClick={handleClick}
        {...buttonProps}
      >
        {children || (
          <>
            <FileIcon className="tiptap-button-icon" />
            <span className="tiptap-button-text">{t("editor.pdf")}</span>
          </>
        )}
      </Button>
    );
  },
);

PdfUploadButton.displayName = "PdfUploadButton";

export default PdfUploadButton;
